import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  useTheme,
  Paper,
  Grid,
  Button,
  IconButton,
  CircularProgress,
  Divider,
  Tooltip,
  Alert,
  Switch,
  FormControlLabel,
  Slider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  VolumeUp as VolumeUpIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import { abrirPainelDetalhes } from '../../utils/abrirPainelDetalhes';
import AcessoRestrito from '../../components/AcessoRestrito';
import api from '../../services/api';
import env from '../../config/env.js';
import { useSocket } from '../../contexts/SocketContext';
import { format, parseISO, isToday, isFuture, isPast } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { normalizarUrlVideo } from './videoUtils';

/**
 * Painel de Atendimento
 * 
 * Página separada da aplicação principal para exibição em outro monitor.
 * Mostra atendimentos aguardando em tempo real.
 */
function PainelAtendimento() {
  const { socket } = useSocket();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [agendamentos, setAgendamentos] = useState([]);
  const [atendimentos, setAtendimentos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [filaAtendimento, setFilaAtendimento] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30); // segundos
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [error, setError] = useState(null);
  
  // Novas configurações do painel
  const [textoTitulo, setTextoTitulo] = useState('Painel de Atendimento');
  const [textoSubtitulo, setTextoSubtitulo] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [somAtivado, setSomAtivado] = useState(true);
  const [somSelecionado, setSomSelecionado] = useState('beep');
  const [configSalva, setConfigSalva] = useState(false);
  const [salvandoConfig, setSalvandoConfig] = useState(false);
  const filaAnteriorRef = useRef([]);
  const painelCarregadoRef = useRef(false);

  // Carregar configurações do backend
  const carregarConfiguracoes = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");

      if (!token || !codHashEmpresa) {
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-second-key': env.SECOND_SECRET_KEY,
        'x-codhash-empresa': codHashEmpresa
      };

      const response = await api.get('/painel/configuracoes', { headers });
      
      if (response.data.success && response.data.data) {
        const config = response.data.data;
        setTextoTitulo(config.textoTitulo || 'Painel de Atendimento');
        setTextoSubtitulo(config.textoSubtitulo || '');
        setVideoUrl(config.videoUrl || '');
        setSomAtivado(config.somAtivado !== undefined ? config.somAtivado : true);
        setSomSelecionado(config.somSelecionado || 'beep');
        setAutoRefresh(config.autoRefresh !== undefined ? config.autoRefresh : true);
        setRefreshInterval(config.refreshInterval || 30);
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      // Se falhar, mantém os valores padrão
    }
  }, []);

  // Carregar configurações ao montar o componente
  useEffect(() => {
    carregarConfiguracoes();
  }, [carregarConfiguracoes]);

  // Função para enviar configurações para o backend e PainelDetalhes
  const enviarConfiguracoes = useCallback(async () => {
    setSalvandoConfig(true);
    
    try {
      const token = localStorage.getItem("token");
      const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");

      if (!token || !codHashEmpresa) {
        setError('Token ou código da empresa não encontrado');
        return;
      }

      const videoNormalizado = normalizarUrlVideo(videoUrl);
      if (videoNormalizado.tipo === 'invalido') {
        setError(videoNormalizado.mensagem);
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-second-key': env.SECOND_SECRET_KEY,
        'x-codhash-empresa': codHashEmpresa
      };

      const configuracoes = {
        textoTitulo,
        textoSubtitulo,
        videoUrl,
        somAtivado,
        somSelecionado,
        autoRefresh,
        refreshInterval
      };

      // Salvar no backend
      const response = await api.post('/painel/configuracoes', configuracoes, { headers });

      if (response.data.success) {
        // Salvar também no localStorage para o PainelDetalhes ler (fallback)
        localStorage.setItem('painelConfiguracoes', JSON.stringify({
          ...configuracoes,
          timestamp: new Date().toISOString()
        }));
        
        // Disparar evento customizado para notificar o PainelDetalhes
        window.dispatchEvent(new CustomEvent('painelConfiguracoesAtualizadas', { 
          detail: configuracoes 
        }));

        setConfigSalva(true);
        
        // Limpar mensagem de sucesso após 3 segundos
        setTimeout(() => {
          setConfigSalva(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      setError(error.response?.data?.message || 'Erro ao salvar configurações');
    } finally {
      setSalvandoConfig(false);
    }
  }, [textoTitulo, textoSubtitulo, videoUrl, somAtivado, somSelecionado, autoRefresh, refreshInterval]);

  const testarPainel = useCallback(() => {
    const testeId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const dadosTeste = {
      ativos: [
        {
          id: `teste-ativo-${testeId}`,
          senha: '0001',
          cliente: 'Paciente de Teste',
          colaborador: 'Profissional de Teste',
          local: 'Sala de Teste',
          status: 'Chamando',
          horario: new Date().toISOString()
        }
      ],
      proximos: [
        {
          id: `teste-proximo-a-${testeId}`,
          senha: '0002',
          cliente: 'Próximo Paciente A',
          colaborador: 'Profissional de Teste',
          local: 'Sala de Teste',
          posicaoNaFila: 1,
          status: 'Aguardando',
          horario: new Date().toISOString()
        },
        {
          id: `teste-proximo-b-${testeId}`,
          senha: '0001',
          cliente: 'Próximo Paciente B',
          colaborador: 'Outro Profissional',
          local: 'Sala 2',
          posicaoNaFila: 1,
          status: 'Aguardando',
          horario: new Date().toISOString()
        }
      ]
    };

    const chaveTeste = `painelTeste:${testeId}`;
    localStorage.setItem(chaveTeste, JSON.stringify(dadosTeste));
    const popup = abrirPainelDetalhes({ testeId });
    if (!popup) {
      localStorage.removeItem(chaveTeste);
      return;
    }

    window.alert('Teste enviado. O painel está funcionando em modo de pré-visualização. Nenhum atendimento real foi alterado.');
  }, []);

  // Função para tocar o som selecionado
  const tocarSom = useCallback(() => {
    const audio = new Audio();
    audio.volume = 0.5;
    
    switch (somSelecionado) {
      case 'beep':
        audio.src = '/sounds/rington1.mp3';
        audio.play().catch(() => {});
        break;
      case 'notification':
        audio.src = '/sounds/rington2.mp3';
        audio.play().catch(() => {});
        break;
      case 'bell':
        audio.src = '/sounds/rington3.mp3';
        audio.play().catch(() => {});
        break;
      case 'chime':
        audio.src = '/sounds/rington4.mp3';
        audio.play().catch(() => {});
        break;
      default:
        // Beep padrão
        const defaultCtx = new (window.AudioContext || window.webkitAudioContext)();
        const defaultOsc = defaultCtx.createOscillator();
        const defaultGain = defaultCtx.createGain();
        defaultOsc.connect(defaultGain);
        defaultGain.connect(defaultCtx.destination);
        defaultOsc.frequency.value = 600;
        defaultOsc.type = 'sine';
        defaultGain.gain.setValueAtTime(0.3, defaultCtx.currentTime);
        defaultGain.gain.exponentialRampToValueAtTime(0.01, defaultCtx.currentTime + 0.3);
        defaultOsc.start(defaultCtx.currentTime);
        defaultOsc.stop(defaultCtx.currentTime + 0.3);
    }
  }, [somSelecionado]);

  // Carregar dados
  const carregarDados = useCallback(async ({ notificarNovaFila = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const { data: estado = {} } = await api.get('/painel/estado');
      const aguardando = estado.proximos || [];
      const atendidos = estado.ultimasChamadas || [];
      const todos = [...aguardando, ...atendidos];
      const idsDaFila = aguardando.map((item) => String(item.id));
      const entrouNovoPaciente = painelCarregadoRef.current
        && idsDaFila.some((id) => !filaAnteriorRef.current.includes(id));

      setAgendamentos(aguardando.map((item) => ({
        _id: item.id, clienteId: item.id, colaboradorId: item.id, dataHoraInicio: item.horario, status: item.status
      })));
      setAtendimentos(todos.map((item) => ({
        id_atendimento: item.id, clienteId: item.id, colaboradorId: item.id,
        ordem_chegada: item.senha, dataHoraInicio: item.horario, status: item.status
      })));
      setClientes(todos.map((item) => ({ id: item.id, nome_completo: item.cliente })));
      setColaboradores(todos.map((item) => ({ id: item.id, nome_completo: item.colaborador })));
      setServicos([]);
      setFilaAtendimento(aguardando.map((item) => ({
        senha: item.senha, cliente: item.cliente, colaborador: item.colaborador,
        atendimentoId: item.id, timestamp: item.horario
      })));

      filaAnteriorRef.current = idsDaFila;
      painelCarregadoRef.current = true;
      if (notificarNovaFila && entrouNovoPaciente && somAtivado) {
        tocarSom();
      }

      setUltimaAtualizacao(new Date());
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [somAtivado, tocarSom]);

  // Carregar dados iniciais
  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  useEffect(() => {
    if (!socket) return undefined;
    let inscrito = false;
    let cancelado = false;

    const assinarPainel = () => {
      socket.emit('painel:assinar', (resultado) => {
        if (cancelado) return;
        if (!resultado?.ok) {
          console.error('Não foi possível assinar as atualizações do painel:', resultado?.error);
          return;
        }
        inscrito = true;
        carregarDados();
      });
    };

    const atualizarPainel = () => {
      carregarDados({ notificarNovaFila: true });
    };

    if (socket.connected) assinarPainel();
    socket.on('connect', assinarPainel);
    socket.on('painel:estado-atualizado', atualizarPainel);
    const fallback = setTimeout(() => {
      if (!inscrito && !cancelado) carregarDados();
    }, 3000);

    return () => {
      cancelado = true;
      clearTimeout(fallback);
      if (socket.connected) socket.emit('painel:cancelar');
      socket.off('connect', assinarPainel);
      socket.off('painel:estado-atualizado', atualizarPainel);
    };
  }, [socket, carregarDados]);
  
  // Nota: Auto-refresh é controlado apenas no PainelDetalhes.jsx através da configuração autoRefresh

  // Configurar fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error('Erro ao entrar em fullscreen:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch(err => {
        console.error('Erro ao sair do fullscreen:', err);
      });
    }
  };

  // Detectar mudanças no fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Ajustar estilos quando em popup/janela separada
  useEffect(() => {
    // Detectar se está sendo aberto em pop-up ou janela separada
    const isPopup = window.opener !== null || window.name === 'painelAtendimento';
    
    if (isPopup) {
      // Remover margens e padding do body para ocupar toda a tela
      document.body.style.margin = '0';
      document.body.style.padding = '0';
      document.body.style.overflow = 'hidden';
      
      // Adicionar classe para identificar janela standalone
      document.body.classList.add('painel-atendimento-standalone');
      
      // Garantir que o html também ocupe toda a altura
      document.documentElement.style.margin = '0';
      document.documentElement.style.padding = '0';
      document.documentElement.style.height = '100%';
      document.documentElement.style.overflow = 'hidden';
    }
    
    return () => {
      document.body.classList.remove('painel-atendimento-standalone');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
    };
  }, []);

  // Filtrar agendamentos aguardando de hoje
  const agendamentosAguardando = agendamentos
    .filter(ag => {
      const atendimento = atendimentos.find(a => a.agendamentoId === ag._id);
      const status = atendimento?.status || ag.status || 'Aguardando';
      return status === 'Aguardando';
    })
    .filter(ag => {
      if (!ag.dataHoraInicio) return false;
      const dataAgendamento = parseISO(ag.dataHoraInicio);
      return isToday(dataAgendamento) || isFuture(dataAgendamento);
    })
    .sort((a, b) => {
      if (!a.dataHoraInicio || !b.dataHoraInicio) return 0;
      return new Date(a.dataHoraInicio) - new Date(b.dataHoraInicio);
    })
    .slice(0, 20); // Limitar a 20

  // Filtrar atendimentos já concluídos (status "Atendido")
  const atendimentosConcluidos = atendimentos
    .filter(a => a.status === 'Atendido' && a.dataHoraInicio)
    .sort((a, b) => {
      // Ordenar por horário de atendimento (mais antigo primeiro)
      const dataA = new Date(a.dataHoraInicio);
      const dataB = new Date(b.dataHoraInicio);
      return dataA - dataB; // Mais antigo primeiro (ordenado por horário)
    })
    .slice(0, 10); // Limitar a 10

  // Função auxiliar para buscar dados
  const buscarCliente = (id) => clientes.find(c => c.id === id || c._id === id);
  const buscarColaborador = (id) => colaboradores.find(c => c.id === id || c._id === id);
  const buscarServico = (id) => servicos.find(s => s.id === id || s._id === id);
  
  // Função para obter senha do atendimento da fila Redis
  const obterSenhaDoAtendimento = (agendamentoId) => {
    // Buscar atendimento associado ao agendamento
    const atendimento = atendimentos.find(a => a.agendamentoId === agendamentoId || a.agendamentoId?.toString() === agendamentoId?.toString());
    
    if (atendimento) {
      // Buscar senha na fila Redis pelo atendimentoId
      const itemFila = filaAtendimento.find(item => 
        item.atendimentoId?.toString() === atendimento.id_atendimento?.toString() ||
        item.atendimentoId?.toString() === atendimento._id?.toString()
      );
      
      if (itemFila && itemFila.senha) {
        return itemFila.senha;
      }
      
      // Se não encontrar na fila, tentar gerar baseado na ordem_chegada (sem letra)
      if (atendimento.ordem_chegada && atendimento.colaboradorId) {
        // Extrair sequência da ordem_chegada
        const colaborador = buscarColaborador(atendimento.colaboradorId);
        if (colaborador) {
          const colaboradorId = colaborador.id || colaborador._id;
          const ordemChegada = atendimento.ordem_chegada.toString();
          const sequencia = parseInt(ordemChegada.slice(colaboradorId.toString().length)) || 1;
          
          // Retornar senha apenas numérica (sem letra)
          return sequencia.toString().padStart(4, '0');
        }
      }
    }
    
    return null;
  };

  return (
    <AcessoRestrito modulo="painelatendimento">
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          height: isFullscreen ? '100vh' : 'auto',
          maxHeight: isFullscreen ? '100vh' : 'none',
          minHeight: isFullscreen ? '100vh' : {
            xs: 'calc(100vh - 160px)',
            sm: 'calc(100vh - 140px)',
            md: 'calc(100vh - 140px)',
            lg: 'calc(100vh - 140px)'
          },
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.default,
          padding: isFullscreen ? 1 : 2,
          boxSizing: 'border-box',
          overflow: isFullscreen ? 'hidden' : 'visible'
        }}
      >
        {/* Cabeçalho */}
        <Paper
          elevation={3}
          sx={{
            p: isFullscreen ? 1.5 : 2,
            mb: isFullscreen ? 1 : 2,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0
          }}
        >
          <Box>
            <Typography variant={isFullscreen ? "h5" : "h4"} component="h1" fontWeight="bold">
              Painel de Atendimento
            </Typography>
            {ultimaAtualizacao && (
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Última atualização: {format(ultimaAtualizacao, "dd/MM/yyyy HH:mm:ss", { locale: ptBR })}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Abrir painel de detalhes">
              <Button
                variant="contained"
                color="secondary"
                startIcon={<InfoIcon />}
                onClick={() => abrirPainelDetalhes()}
                sx={{ mr: 1 }}
              >
                Detalhes
              </Button>
            </Tooltip>
            <Tooltip title="Atualizar agora">
              <IconButton
                onClick={carregarDados}
                disabled={loading}
                sx={{ color: 'inherit' }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: 'inherit' }} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={isFullscreen ? "Sair do modo tela cheia" : "Modo tela cheia"}>
              <IconButton
                onClick={toggleFullscreen}
                sx={{ color: 'inherit' }}
              >
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        {/* Erro */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Lista de atendimentos aguardando e concluídos */}
        {loading && agendamentos.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 0 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ flex: isFullscreen ? 1 : 'none', minHeight: 0, overflow: isFullscreen ? 'hidden' : 'visible' }}>
            {/* Coluna Esquerda - Atendimentos Aguardando */}
            <Grid item xs={12} md="auto" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: '100%', overflow: 'auto', flex: '0 0 auto' }}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: isFullscreen ? 1.5 : 2, 
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  maxHeight: '100%',
                  overflow: 'auto',
                  minHeight: 0
                }}
              >
                <Typography variant="h6" gutterBottom fontWeight="bold" color="primary.main" sx={{ flexShrink: 0 }}>
                  Pacientes Aguardando
                </Typography>
                <Divider sx={{ mb: 2, flexShrink: 0 }} />
                
                {agendamentosAguardando.length === 0 ? (
                  <Alert severity="info">
                    Nenhum atendimento aguardando no momento.
                  </Alert>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 1.5, 
                    overflowY: 'auto', 
                    overflowX: 'hidden',
                    flex: 1,
                    minHeight: 0,
                    pr: 1,
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'} ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                    '&::-webkit-scrollbar': {
                      width: '12px'
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                      borderRadius: '6px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                      borderRadius: '6px',
                      border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'
                      }
                    }
                  }}>
                    {agendamentosAguardando.map((agendamento) => {
                      const cliente = buscarCliente(agendamento.clienteId);
                      const colaborador = buscarColaborador(agendamento.colaboradorId);
                      const dataHora = agendamento.dataHoraInicio ? parseISO(agendamento.dataHoraInicio) : null;
                      const estaAtrasado = dataHora && isPast(dataHora) && !isToday(dataHora);
                      const prontoParaAtendimento = dataHora && isPast(dataHora) && isToday(dataHora);

                      // Definir cor da borda baseado no status
                      let borderColor = 'divider';
                      let backgroundColor = 'transparent';
                      if (estaAtrasado) {
                        borderColor = 'error.main';
                        backgroundColor = theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.1)' : 'rgba(211, 47, 47, 0.05)';
                      } else if (prontoParaAtendimento) {
                        borderColor = 'success.main';
                        backgroundColor = theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)';
                      }

                      return (
                        <Paper
                          key={agendamento._id || agendamento.id}
                          elevation={1}
                          sx={{
                            borderLeft: '4px solid',
                            borderLeftColor: borderColor,
                            backgroundColor: backgroundColor,
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            py: 1.5,
                            px: 2,
                            '&:hover': {
                              transform: 'translateX(4px)',
                              boxShadow: 3
                            }
                          }}
                        >
                          <Typography variant="body1" fontWeight="bold" gutterBottom>
                            {cliente?.nome_completo || cliente?.st_nome || 'Cliente não encontrado'}
                          </Typography>
                          {colaborador && (
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <strong>Colaborador:</strong> {colaborador.nome_completo || colaborador.st_nome || 'N/A'}
                            </Typography>
                          )}
                          {dataHora && (
                            <Typography variant="body2" color="text.secondary">
                              <strong>Horário:</strong> {format(dataHora, "HH:mm", { locale: ptBR })} - {format(dataHora, "dd/MM/yyyy", { locale: ptBR })}
                            </Typography>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Coluna Central - Atendimentos Concluídos */}
            <Grid item xs={12} md="auto" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: '100%', overflow: 'auto', flex: '0 0 auto', minWidth: { md: '300px' } }}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: isFullscreen ? 1.5 : 2, 
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  maxHeight: '100%',
                  overflow: 'auto',
                  minHeight: 0
                }}
              >
                <Typography variant="h6" gutterBottom fontWeight="bold" color="success.main" sx={{ flexShrink: 0 }}>
                  Pacientes Atendidos
                </Typography>
                <Divider sx={{ mb: 2, flexShrink: 0 }} />
                
                {atendimentosConcluidos.length === 0 ? (
                  <Alert severity="info">
                    Nenhum paciente atendido ainda hoje.
                  </Alert>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 1.5, 
                    overflowY: 'auto', 
                    overflowX: 'hidden',
                    flex: 1,
                    minHeight: 0,
                    pr: 1,
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'} ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                    '&::-webkit-scrollbar': {
                      width: '12px'
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                      borderRadius: '6px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                      borderRadius: '6px',
                      border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'
                      }
                    }
                  }}>
                    {atendimentosConcluidos.map((atendimento) => {
                      const agendamento = agendamentos.find(a => a._id === atendimento.agendamentoId);
                      const cliente = buscarCliente(atendimento.clienteId || agendamento?.clienteId);
                      const colaborador = buscarColaborador(atendimento.colaboradorId || agendamento?.colaboradorId);
                      const dataHora = atendimento.dataHoraInicio ? parseISO(atendimento.dataHoraInicio) : null;

                      return (
                        <Paper
                          key={atendimento.id_atendimento || atendimento._id}
                          elevation={1}
                          sx={{
                            borderLeft: '4px solid',
                            borderLeftColor: 'success.main',
                            backgroundColor: theme.palette.mode === 'dark' 
                              ? 'rgba(76, 175, 80, 0.1)' 
                              : 'rgba(76, 175, 80, 0.05)',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            py: 1.5,
                            px: 2,
                            '&:hover': {
                              transform: 'translateX(4px)',
                              boxShadow: 3
                            }
                          }}
                        >
                          <Typography variant="body1" fontWeight="bold" gutterBottom>
                            {cliente?.nome_completo || cliente?.st_nome || 'Cliente não encontrado'}
                          </Typography>
                          {colaborador && (
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <strong>Colaborador:</strong> {colaborador.nome_completo || colaborador.st_nome || 'N/A'}
                            </Typography>
                          )}
                          {dataHora && (
                            <Typography variant="body2" color="text.secondary">
                              <strong>Horário:</strong> {format(dataHora, "HH:mm", { locale: ptBR })} - {format(dataHora, "dd/MM/yyyy", { locale: ptBR })}
                            </Typography>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Coluna Direita - Configurações do Painel */}
            <Grid item xs={12} md sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: isFullscreen ? '100%' : 'none', overflow: isFullscreen ? 'auto' : 'visible', flex: '1 1 auto' }}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: isFullscreen ? 1.5 : 2, 
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  maxHeight: isFullscreen ? '100%' : 'none',
                  overflowY: isFullscreen ? 'hidden' : 'visible',
                  overflowX: 'hidden',
                  minHeight: 0,
                  scrollbarWidth: 'thin',
                  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'} ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                  '&::-webkit-scrollbar': {
                    width: '12px'
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                    borderRadius: '6px'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                    borderRadius: '6px',
                    border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'
                    }
                  }
                }}
              >
                <Typography variant="h6" gutterBottom fontWeight="bold" color="info.main" sx={{ flexShrink: 0 }}>
                  Configurações do Painel
                </Typography>
                <Divider sx={{ mb: 2, flexShrink: 0 }} />
                
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 3,
                  flex: 1,
                  minHeight: 0,
                  overflowY: isFullscreen ? 'auto' : 'visible',
                  overflowX: 'hidden',
                  pr: 1,
                  pb: 2
                }}>
                  {/* Textos do Painel */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Textos do Painel
                    </Typography>
                    <TextField
                      fullWidth
                      label="Título do Painel"
                      value={textoTitulo}
                      onChange={(e) => setTextoTitulo(e.target.value)}
                      size="small"
                      sx={{ mb: 2 }}
                      variant="outlined"
                    />
                    <TextField
                      fullWidth
                      label="Subtítulo (opcional)"
                      value={textoSubtitulo}
                      onChange={(e) => setTextoSubtitulo(e.target.value)}
                      size="small"
                      multiline
                      rows={2}
                      variant="outlined"
                    />
                  </Box>

                  <Divider />

                  {/* Vídeo */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Vídeo para Exibição
                    </Typography>
                    <TextField
                      fullWidth
                      label="URL do Vídeo"
                      value={videoUrl}
                      onChange={(e) => setVideoUrl(e.target.value)}
                      size="small"
                      placeholder="https://exemplo.com/video.mp4 ou URL do YouTube"
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Aceita links do YouTube (vídeo, Shorts ou transmissão ao vivo) e URLs HTTP/HTTPS de vídeo.
                    </Typography>
                  </Box>

                  <Divider />

                  {/* Som */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Som de Atendimento
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={somAtivado}
                          onChange={(e) => setSomAtivado(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Ativar som ao trocar senha"
                    />
                    {somAtivado && (
                      <>
                        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                          <InputLabel>Selecionar Som</InputLabel>
                          <Select
                            value={somSelecionado}
                            onChange={(e) => setSomSelecionado(e.target.value)}
                            label="Selecionar Som"
                          >
                            <MenuItem value="beep">Beep (Padrão)</MenuItem>
                            <MenuItem value="notification">Notificação</MenuItem>
                            <MenuItem value="bell">Sino</MenuItem>
                            <MenuItem value="chime">Sino Musical</MenuItem>
                          </Select>
                        </FormControl>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<VolumeUpIcon />}
                          onClick={tocarSom}
                          sx={{ mt: 2 }}
                          fullWidth
                        >
                          Ouvir Som
                        </Button>
                      </>
                    )}
                  </Box>

                  <Divider />

                  {/* Auto Refresh */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Atualização Automática
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Controla a atualização automática no Painel de Detalhes
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={autoRefresh}
                          onChange={(e) => setAutoRefresh(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Ativar atualização automática no Painel de Detalhes"
                    />
                    {autoRefresh && (
                      <Box sx={{ mt: 2, px: 1 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Intervalo: {refreshInterval} segundos
                        </Typography>
                        <Slider
                          value={refreshInterval}
                          onChange={(e, newValue) => setRefreshInterval(newValue)}
                          min={10}
                          max={120}
                          step={10}
                          marks={[
                            { value: 10, label: '10s' },
                            { value: 30, label: '30s' },
                            { value: 60, label: '60s' },
                            { value: 120, label: '120s' }
                          ]}
                          valueLabelDisplay="auto"
                          color="primary"
                        />
                      </Box>
                    )}
                  </Box>

                  <Divider />

                  {/* Modo Fullscreen */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Exibição
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isFullscreen}
                          onChange={toggleFullscreen}
                          color="primary"
                        />
                      }
                      label="Modo Tela Cheia"
                    />
                  </Box>

                  <Divider />

                  {/* Informações */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Estatísticas
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Aguardando:</strong> {agendamentosAguardando.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        <strong>Atendidos hoje:</strong> {atendimentos.filter(a => a.status === 'Atendido').length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        <strong>Colaboradores:</strong> {colaboradores.length}
                      </Typography>
                      {ultimaAtualizacao && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Última atualização:<br />
                          {format(ultimaAtualizacao, "dd/MM/yyyy HH:mm:ss", { locale: ptBR })}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                </Box>

                {/* Ações sempre visíveis fora da área rolável */}
                <Box sx={{ flexShrink: 0, pt: 2, mt: 1, borderTop: 1, borderColor: 'divider' }}>
                    {configSalva && (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        Configurações enviadas com sucesso para o Painel de Detalhes!
                      </Alert>
                    )}
                    <Button
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      size="large"
                      onClick={testarPainel}
                      sx={{ mb: 1.5, py: 1.25, fontWeight: 'bold', textTransform: 'none' }}
                    >
                      Testar painel
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="large"
                      onClick={enviarConfiguracoes}
                      disabled={salvandoConfig}
                      sx={{ 
                        py: 1.5,
                        fontWeight: 'bold',
                        textTransform: 'none'
                      }}
                    >
                      {salvandoConfig ? 'Salvando...' : 'Enviar Configurações'}
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                      As alterações serão aplicadas no Painel de Detalhes
                    </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>
    </AcessoRestrito>
  );
}

export default PainelAtendimento;
